// Deal Decline Workflow hooks.
//
// Reads the orthogonal decline state and exposes the three transition
// mutations (propose / withdraw / confirm). Each mutation invalidates the
// deal detail + the decline state + the pipeline lists so the banners,
// header menu, and kanban styling all refresh in-place without a manual
// reload. Errors propagate to the caller's onError; the toast wrapper in
// the modal surfaces the friendly server message.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dealsApi } from "@/api/deals";
import { dealKeys } from "./useDeals";
import type {
  ConfirmDeclineRequest,
  DealDeclineStateDto,
  ProposeDeclineRequest,
} from "@/types/deals";

export const declineKeys = {
  state: (dealId: string) => [...dealKeys.all, "decline", dealId] as const,
};

export function useDeclineState(dealId: string | undefined) {
  return useQuery<DealDeclineStateDto>({
    queryKey: declineKeys.state(dealId ?? ""),
    queryFn: () => dealsApi.declineState(dealId!),
    enabled: !!dealId,
  });
}

function useInvalidateDecline(dealId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: declineKeys.state(dealId) });
    qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
    qc.invalidateQueries({ queryKey: dealKeys.lists() });
  };
}

export function useProposeDecline(dealId: string) {
  const invalidate = useInvalidateDecline(dealId);
  return useMutation<DealDeclineStateDto, Error, ProposeDeclineRequest>({
    mutationFn: (body) => dealsApi.proposeDecline(dealId, body),
    onSuccess: invalidate,
  });
}

export function useWithdrawDeclineProposal(dealId: string) {
  const invalidate = useInvalidateDecline(dealId);
  return useMutation<DealDeclineStateDto, Error, void>({
    mutationFn: () => dealsApi.withdrawDeclineProposal(dealId),
    onSuccess: invalidate,
  });
}

export function useConfirmDecline(dealId: string) {
  const invalidate = useInvalidateDecline(dealId);
  return useMutation<DealDeclineStateDto, Error, ConfirmDeclineRequest>({
    mutationFn: (body) => dealsApi.confirmDecline(dealId, body),
    onSuccess: invalidate,
  });
}
